import Joi, { MAX_CHARACTERS } from '../../../helpers/joiCommonSchemas';
import { FieldType } from '../../../services/form/constants';

export const VALIDATION_MESSAGE = {
  FIELD_EMPTY: 'This field is not allowed to be empty',
  TEXT_MIN_LENGTH: 'Text is too short',
  TEXT_MAX_LENGTH: 'Text is too long',
  NOT_A_NUMBER: 'This field only allows numbers',
  SAFE_NUMBER: 'Must be a safe number',
  MULTIPLE_CHOICE_MISSING: 'Please select an option',
  MULTIPLE_CHOICE_CORRECT_ANSWER: 'This field requires the correct option to be selected',
  WRONG_SELECTION: 'Please select the right choice',
  MISSING_SELECTION: 'It does not contain the required match',
  BOOLEAN_NO: 'This field requires the answer to be NO',
  BOOLEAN_YES: 'This field requires the answer to be YES',
};

function requiredSchema(schema, required) {
  if (required) {
    return schema.required();
  } else {
    return schema.allow('');
  }
}

function optionRequiredSchema(schema, required, selectiveChoiceValues) {
  if (!required && selectiveChoiceValues.length > 0) {
    return schema.required();
  } else if (required) {
    return schema.required();
  } else {
    return schema.optional().allow('');
  }
}

function attachAllowedValues(schema, allowedValues) {
  if (allowedValues?.length === 1 && allowedValues?.includes(false)) {
    return schema.valid(false).messages({
      'any.only': VALIDATION_MESSAGE.BOOLEAN_NO,
    });
  } else if (allowedValues?.length === 1 && allowedValues?.includes(true)) {
    return schema.valid(true).messages({
      'any.only': VALIDATION_MESSAGE.BOOLEAN_YES,
    });
  } else {
    return schema.valid(true, false).messages({
      'any.only': VALIDATION_MESSAGE.FIELD_EMPTY,
    });
  }
}

function attachTextSchema(schema, required) {
  const _schema = schema.string().trim().max(MAX_CHARACTERS).messages({
    'string.empty': VALIDATION_MESSAGE.FIELD_EMPTY,
    'string.min': VALIDATION_MESSAGE.TEXT_MIN_LENGTH,
    'string.max': VALIDATION_MESSAGE.TEXT_MAX_LENGTH,
    'any.required': VALIDATION_MESSAGE.FIELD_EMPTY,
  });

  return requiredSchema(_schema, required);
}

function attachNumberSchema(schema, required) {
  const _schema = schema.number().messages({
    'number.empty': VALIDATION_MESSAGE.FIELD_EMPTY,
    'number.base': VALIDATION_MESSAGE.NOT_A_NUMBER,
    'number.unsafe': VALIDATION_MESSAGE.TEXT_MAX_LENGTH,
    'any.required': VALIDATION_MESSAGE.FIELD_EMPTY,
  });

  return requiredSchema(_schema, required);
}

function attachBooleanSwitcherSchema(schema, required, allowedValues) {
  const _schema = attachAllowedValues(schema, allowedValues).messages({
    'any.required': VALIDATION_MESSAGE.FIELD_EMPTY,
  });

  return requiredSchema(_schema, required);
}

function attachMultipleChoiceSchema(schema, required, options, allowedOptions) {
  const optionsSchemasArray = [];
  const optionsNamesArray = [];

  allowedOptions?.forEach((option) => {
    optionsSchemasArray.push({ label: options[option], value: option });
    optionsNamesArray.push(options[option]);
  });

  const _schema = schema
    .object()
    .keys({
      label: Joi.string(),
      value: Joi.alternatives().try(Joi.number(), Joi.string()),
    })
    .valid(...optionsSchemasArray)
    .messages({
      'object.base': VALIDATION_MESSAGE.MULTIPLE_CHOICE_MISSING,
      'any.required': VALIDATION_MESSAGE.FIELD_EMPTY,
      'any.only': `This field requires ${optionsNamesArray
        .join(', ')
        .replace(/, ((?:.(?!, ))+)$/, ' or $1')} to be selected`,
    });

  return requiredSchema(_schema, required);
}

function attachSelectiveChoiceSchema(schema, required, options, allowedOptions, selectiveChoiceValues) {
  const optionsSchemasArray = [];
  const optionsNamesArray = [];

  allowedOptions?.forEach((option) => {
    optionsSchemasArray.push(
      optionRequiredSchema(Joi.number().valid(options[option]), required, selectiveChoiceValues),
    );
    optionsNamesArray.push(options[option]);
  });

  const _schema = schema
    .array()
    .items(...optionsSchemasArray, Joi.string().allow(''))
    .min(required ? 1 : 0)
    .messages({
      'any.required': VALIDATION_MESSAGE.FIELD_EMPTY,
      'array.min': VALIDATION_MESSAGE.FIELD_EMPTY,
      'array.includesRequiredUnknowns': `This field requires ${optionsNamesArray
        .join(', ')
        .replace(/, ((?:.(?!, ))+)$/, ' and $1')} to be selected`,
    });

  return requiredSchema(_schema, required);
}

export default function generateSiteCheckInFormSchema(fields = [], selectiveChoiceValues) {
  let dynamicSchema = {};

  for (const field of fields) {
    const fieldId = field.id;
    const initSchema = Joi;

    switch (field.type) {
      case FieldType.TEXT:
        dynamicSchema = { ...dynamicSchema, [fieldId]: attachTextSchema(initSchema, field.required) };
        break;
      case FieldType.NUMBER:
        dynamicSchema = { ...dynamicSchema, [fieldId]: attachNumberSchema(initSchema, field.required) };
        break;
      case FieldType.BOOLEAN_SWITCH:
        dynamicSchema = {
          ...dynamicSchema,
          [fieldId]: attachBooleanSwitcherSchema(initSchema, field.required, field.allowedValues),
        };
        break;
      case FieldType.MULTIPLE_CHOICE:
        dynamicSchema = {
          ...dynamicSchema,
          [fieldId]: attachMultipleChoiceSchema(initSchema, field.required, field.options, field.allowedOptions),
        };
        break;
      case FieldType.SELECTIVE_CHOICE:
        dynamicSchema = {
          ...dynamicSchema,
          [fieldId]: attachSelectiveChoiceSchema(
            initSchema,
            field.required,
            field.options,
            field.allowedOptions,
            selectiveChoiceValues,
          ),
        };
        break;
    }
  }

  return Joi.object(dynamicSchema);
}
